import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { VideoSessionStore } from '../stores/video-session.store';
import { VideoSession } from '../../models/app/video-session';
import { map, Observable, switchMap, tap } from 'rxjs';
import {
  BitrateResult,
  BitrateStats,
  ConnectivityResult,
  QualityStats,
} from '../../models/app/precall/bitrate-result';
import { MapPipe } from '../../shared/pipes/map.pipe';
import type {
  OTError,
  PublisherStats,
  PublisherStatsArr,
  Subscriber,
  SubscriberStats,
} from '@opentok/client';
import { OpenTokLoaderService } from './opentok-loader.service';
import { AudioBitrateStore } from '../stores/audio-bitrate.store';

const TEST_DURATION_MS = 10_000;
const POLL_INTERVAL_MS = 1_000;
// Extra buffer beyond TEST_DURATION_MS before the safety timeout fires.
// Covers cases where the publish callback never fires (e.g. on iOS).
const SAFETY_BUFFER_MS = 5_000;

@Injectable({
  providedIn: 'root',
})
export class VideoSessionService {
  private readonly otLoader = inject(OpenTokLoaderService);
  private readonly mapPipe = inject(MapPipe);
  private readonly http = inject(HttpClient);
  readonly store = inject(VideoSessionStore);
  readonly audioBitRateStore = inject(AudioBitrateStore);

  create(): Observable<VideoSession> {
    return this.http
      .post<VideoSession>('/api/v1/sessions', null)
      .pipe(tap((session) => this.store.update(session)));
  }

  connectivityTest(): Observable<ConnectivityResult> {
    const session: VideoSession | null = this.session;
    if (session == null) throw new Error('No video session has been registered');

    return this.otLoader.load().pipe(
      switchMap(
        (OT) =>
          new Observable<ConnectivityResult>((observer) => {
            const otSession = OT.initSession(session.applicationId, session.sessionId);
            otSession.connect(session.token, (err?: OTError) => {
              if (err) {
                observer.error(err);
              } else {
                observer.next({ success: true });
                observer.complete();
              }
              otSession.disconnect();
            });
            return () => otSession.disconnect();
          }),
      ),
    );
  }

  qualityTest(video = false): Observable<BitrateResult> {
    const session: VideoSession | null = this.session;
    if (session == null) throw new Error('No video session has been registered');

    return this.otLoader.load().pipe(
      switchMap(
        (OT) =>
          new Observable<{ partial?: BitrateStats; complete?: QualityStats }>((observer) => {
            // Single session connection: subscribing to our own stream is enabled via
            // { testNetwork: true } in the subscriber properties, which is the OpenTok
            // built-in loopback mechanism for quality diagnostics.
            const otSession = OT.initSession(session.applicationId, session.sessionId);

            const publisher = OT.initPublisher(undefined, {
              audioSource: undefined,
              videoSource: video,
              publishAudio: true,
              publishVideo: video,
              width: 1,
              height: 1,
              insertDefaultUI: false,
              showControls: false,
            });

            let subscriber: Subscriber | null = null;
            let pollInterval: ReturnType<typeof setInterval> | null = null;
            let safetyTimeout: ReturnType<typeof setTimeout> | null = null;
            let testDone = false;
            let startTime = 0;
            let lastBytesSent = 0;
            let lastBytesReceived = 0;
            let lastPacketsSent = 0;
            let lastPacketsLost = 0;
            let lastPartialStats: BitrateStats | null = null;

            const cleanup = () => {
              if (pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
              }
              if (safetyTimeout) {
                clearTimeout(safetyTimeout);
                safetyTimeout = null;
              }
              try {
                publisher.destroy();
              } catch {
                /* ignore */
              }
              try {
                otSession.disconnect();
              } catch {
                /* ignore */
              }
            };

            const collectStats = () => {
              let pubStats: PublisherStats | null = null;
              let subStats: SubscriberStats | null = null;
              let pubDone = false;
              let subDone = subscriber === null;

              const maybeEmit = () => {
                if (!pubDone || !subDone) return;
                if (testDone) return;

                lastBytesSent = pubStats?.audio?.bytesSent ?? lastBytesSent;
                lastPacketsSent = pubStats?.audio?.packetsSent ?? lastPacketsSent;
                lastPacketsLost = pubStats?.audio?.packetsLost ?? lastPacketsLost;
                lastBytesReceived = subStats?.audio?.bytesReceived ?? lastBytesReceived;

                const partial: BitrateStats = {
                  timestamp: Date.now(),
                  audio: { bytesSent: lastBytesSent, bytesReceived: lastBytesReceived },
                };
                lastPartialStats = partial;
                this.audioBitRateStore.update({ partial });
              };

              publisher.getStats((error?: OTError, stats?: PublisherStatsArr) => {
                pubStats = error ? null : (stats?.[0]?.stats ?? null);
                pubDone = true;
                maybeEmit();
              });

              if (subscriber) {
                subscriber.getStats((err?: OTError, stats?: SubscriberStats) => {
                  subStats = err ? null : (stats ?? null);
                  subDone = true;
                  maybeEmit();
                });
              }
            };

            const finalize = () => {
              testDone = true;
              if (pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
              }

              if (lastPartialStats == null) {
                observer.error(new Error('No stats received during quality test.'));
                cleanup();
                return;
              }

              const durationSeconds = (Date.now() - startTime) / 1000;
              const bitrate = durationSeconds > 0 ? (lastBytesSent * 8) / durationSeconds : 0;
              const totalPackets = lastPacketsSent + lastPacketsLost;
              const packetLossRatio = totalPackets > 0 ? lastPacketsLost / totalPackets : 0;

              observer.next({
                complete: { audio: { bitrate, packetLossRatio } },
              });
              observer.complete();
              cleanup();
            };

            // Top-level safety net: terminates the observable even if the connect or
            // publish callbacks never fire (e.g. on iOS with conflicting audio constraints).
            safetyTimeout = setTimeout(() => {
              if (testDone) return;
              testDone = true;
              observer.error(new Error('Quality test timed out.'));
              cleanup();
            }, TEST_DURATION_MS + SAFETY_BUFFER_MS);

            otSession.connect(session.token, (err?: OTError) => {
              if (err) {
                observer.error(err);
                cleanup();
                return;
              }

              otSession.publish(publisher, (pubErr?: OTError) => {
                if (pubErr) {
                  observer.error(pubErr);
                  cleanup();
                  return;
                }

                // streamCreated is NOT dispatched to the publisher's own connection.
                // Subscribe directly via publisher.stream using testNetwork:true, which
                // is the OpenTok loopback mechanism for quality diagnostics.
                if (!publisher.stream) {
                  observer.error(new Error('Publisher stream unavailable after publish'));
                  cleanup();
                  return;
                }

                subscriber = otSession.subscribe(publisher.stream, undefined, {
                  testNetwork: true,
                  subscribeToVideo: false,
                  audioVolume: 0,
                });

                startTime = Date.now();
                pollInterval = setInterval(collectStats, POLL_INTERVAL_MS);
                setTimeout(finalize, TEST_DURATION_MS);
              });
            });

            return () => cleanup();
          }),
      ),
      map(this.evaluateQuality.bind(this)),
    );
  }

  get session(): VideoSession | null {
    const session = this.store.snapshot;
    if (!session || !session.applicationId || !session.sessionId || !session.token) {
      console.warn('Can not start precall tests, missing session credentials from API');
      return null;
    }
    return session;
  }

  private evaluateQuality(result: {
    partial?: BitrateStats;
    complete?: QualityStats;
  }): BitrateResult {
    const minBitrate = 6 * 1_000;
    const maxBitrate = 40 * 1_000;

    const bitrate = result.complete?.audio.bitrate ?? 0;
    const packetLossRatio = result.complete?.audio.packetLossRatio ?? 0;

    let mos = this.mapPipe.transform(bitrate, minBitrate, maxBitrate, 1, 5);

    if (packetLossRatio > 0) {
      const penalty = Math.min(packetLossRatio / 0.05, 1) * 1;
      mos = mos - penalty;
    }

    mos = Math.max(1, Math.min(5, mos));

    return { partial: result.partial, complete: result.complete, mos };
  }
}
